<template>
    <!-- Breadcrumb Navigation -->
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> 
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <router-link :to="{ name: 'pages.admin.products' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.products") }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("common.create") }}</span>
        </button>
    </div>

    <!-- Main Content -->
    <div class="tw-p-2">
        <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-gray-200">
            <!-- Header Section -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                <div>
                    <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">{{ $t('forms.product.create-title') }}</h1>
                    <p class="tw-text-sm tw-text-gray-600">{{ $t('forms.product.create-subtitle') }}</p>
                </div>
                
                <!-- Back Button -->
                <a-button
                    @click="goBack"
                    intent="light"
                    left-icon="solar:arrow-left-linear"
                >
                    {{ $t('common.back-to-products') }}
                </a-button>
            </div>

            <!-- Skeleton Loader -->
            <div v-if="categoriesLoading || companiesLoading" class="tw-w-full">
                <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
                    <!-- Left Column - Form Skeleton -->
                    <div class="xl:tw-col-span-8 tw-space-y-6">
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <div class="tw-w-40 tw-h-6 tw-bg-gray-200 tw-rounded tw-mb-6"></div>
                            <div class="tw-space-y-6">
                                <div>
                                    <div class="tw-w-24 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                    <div class="tw-w-full tw-h-10 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-64 tw-h-3 tw-bg-gray-100 tw-rounded tw-mt-1"></div>
                                </div>
                                <div>
                                    <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                    <div class="tw-w-full tw-h-24 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-72 tw-h-3 tw-bg-gray-100 tw-rounded tw-mt-1"></div>
                                </div>
                                <div>
                                    <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                    <div class="tw-w-full tw-h-10 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-80 tw-h-3 tw-bg-gray-100 tw-rounded tw-mt-1"></div>
                                </div>
                            </div>
                        </div>
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <div class="tw-w-36 tw-h-6 tw-bg-gray-200 tw-rounded tw-mb-6"></div>
                            <div>
                                <div class="tw-w-28 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                <div class="tw-w-full tw-h-10 tw-bg-gray-200 tw-rounded"></div>
                                <div class="tw-w-56 tw-h-3 tw-bg-gray-100 tw-rounded tw-mt-1"></div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Right Column - Image Skeleton -->
                    <div class="xl:tw-col-span-4">
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <div class="tw-w-28 tw-h-6 tw-bg-gray-200 tw-rounded tw-mb-6"></div>
                            <div class="tw-w-full tw-h-80 tw-bg-gray-200 tw-rounded-lg tw-mb-6"></div>
                            <div class="tw-bg-gray-100 tw-rounded-lg tw-p-4">
                                <div class="tw-w-20 tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2"></div>
                                <div class="tw-space-y-2">
                                    <div class="tw-w-full tw-h-3 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-5/6 tw-h-3 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-4/5 tw-h-3 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-w-3/4 tw-h-3 tw-bg-gray-200 tw-rounded"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Form Content -->
            <form v-else @submit.prevent="createProduct" class="tw-w-full">
                <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-8">
                    <!-- Left Column - Product Information -->
                    <div class="xl:tw-col-span-8 tw-space-y-6">
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">{{ $t('forms.product.product-information') }}</h2>
                            
                            <div class="tw-space-y-6">
                                <!-- Product Title -->
                                <FormField
                                    v-model="productForm.title"
                                    type="text"
                                    :label="$t('forms.product.title')"
                                    :placeholder="$t('forms.product.title-placeholder')"
                                    left-icon="solar:box-linear"
                                    :error="productErrors.title"
                                    required
                                    :help="$t('forms.product.title-help')"
                                />

                                <!-- Product Description -->
                                <FormField
                                    v-model="productForm.description"
                                    type="textarea"
                                    :label="$t('forms.product.description')"
                                    :placeholder="$t('forms.product.description-placeholder')"
                                    :error="productErrors.description"
                                    rows="6"
                                    :help="$t('forms.product.description-help')"
                                />

                                <!-- Categories -->
                                <FormMultiSelect
                                    v-model="productForm.categories"
                                    :options="categoryOptions"
                                    :label="$t('forms.product.categories')"
                                    :placeholder="$t('forms.product.categories-placeholder')"
                                    left-icon="solar:widget-bold-duotone"
                                    :error="productErrors.categories"
                                    :help="$t('forms.product.categories-help')"
                                    :loading="categoriesLoading"
                                />
                            </div>
                        </div>

                        <!-- Company Selection -->
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">{{ $t('forms.product.company-assignment') }}</h3>
                            
                            <!-- Company Selection Field -->
                            <FormSelect
                                v-model="productForm.company_id"
                                :options="companyOptions"
                                :label="$t('forms.product.assign-to-company')"
                                :placeholder="$t('forms.product.company-placeholder')"
                                left-icon="solar:buildings-bold-duotone"
                                :error="productErrors.company_id"
                                :help="$t('forms.product.company-help')"
                                :loading="companiesLoading"
                                required
                            />
                        </div>
                    </div>

                    <!-- Right Column - Product Image -->
                    <div class="xl:tw-col-span-4 tw-space-y-6">
                        <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6">
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-6">{{ $t('forms.product.product-image') }}</h2>
                            
                            <!-- Image Upload Area -->
                            <div class="tw-mb-6">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">
                                    {{ $t('forms.product.image-label') }} <span class="tw-text-gray-500">({{ $t('common.optional') }})</span>
                                </label>
                                
                                <div
                                    @drop.prevent="handleImageDrop"
                                    @dragover.prevent="isDragging = true"
                                    @dragleave.prevent="isDragging = false"
                                    @click="triggerImageInput"
                                    :class="[
                                        'tw-relative tw-w-full tw-h-80 tw-border-2 tw-border-dashed tw-rounded-lg tw-cursor-pointer tw-transition-all tw-duration-200 tw-flex tw-items-center tw-justify-center',
                                        isDragging ? 'tw-border-primary-500 tw-bg-primary-50' : 'tw-border-gray-300 hover:tw-border-primary-400 tw-bg-gray-50 hover:tw-bg-gray-100',
                                        productForm.imagePreview ? 'tw-border-solid tw-bg-white' : ''
                                    ]"
                                >
                                    <!-- Image Preview -->
                                    <div v-if="productForm.imagePreview" class="tw-relative tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
                                        <img
                                            :src="productForm.imagePreview"
                                            alt="Product preview"
                                            class="tw-max-w-full tw-max-h-full tw-object-contain tw-rounded-lg"
                                        />
                                        <button
                                            @click.stop="removeImage"
                                            type="button"
                                            class="tw-absolute tw-top-3 tw-right-3 tw-w-10 tw-h-10 tw-bg-red-500 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center hover:tw-bg-red-600 tw-transition-colors tw-shadow-lg"
                                        >
                                            <icon icon="solar:close-circle-bold" class="tw-text-xl" />
                                        </button>
                                    </div>
                                    
                                    <!-- Upload Placeholder - Centered -->
                                    <div v-else class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-p-8">
                                        <div class="tw-mb-4">
                                            <icon icon="solar:camera-add-bold-duotone" class="tw-text-6xl tw-text-gray-400" />
                                        </div>
                                        <h3 class="tw-text-lg tw-font-medium tw-text-gray-700 tw-mb-2">
                                            {{ $t('forms.product.upload-image') }}
                                        </h3>
                                        <p class="tw-text-sm tw-text-gray-500 tw-mb-4 tw-max-w-xs">
                                            {{ $t('forms.product.upload-description') }}
                                        </p>
                                        <div class="tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-bg-primary-500 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-primary-600 tw-transition-colors">
                                            <icon icon="solar:upload-bold-duotone" class="tw-mr-2" />
                                            {{ $t('forms.product.choose-image') }}
                                        </div>
                                        <p class="tw-text-xs tw-text-gray-400 tw-mt-3">
                                            {{ $t('forms.product.file-format') }}
                                        </p>
                                    </div>
                                    
                                    <input
                                        ref="imageInput"
                                        type="file"
                                        accept="image/*"
                                        @change="handleImageSelect"
                                        class="tw-hidden"
                                    />
                                </div>
                                
                                <p v-if="productErrors.thumbnail_url" class="tw-text-red-500 tw-text-sm tw-mt-2">
                                    {{ productErrors.thumbnail_url }}
                                </p>
                            </div>

                            <!-- Image Upload Tips -->
                            <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                                <h3 class="tw-text-sm tw-font-medium tw-text-blue-900 tw-mb-2 tw-flex tw-items-center">
                                    <icon icon="solar:info-circle-bold" class="tw-mr-2" />
                                    {{ $t('forms.product.image-tips') }}
                                </h3>
                                <ul class="tw-text-sm tw-text-blue-800 tw-space-y-1">
                                    <li>• {{ $t('forms.product.image-tip-1') }}</li>
                                    <li>• {{ $t('forms.product.image-tip-2') }}</li>
                                    <li>• {{ $t('forms.product.image-tip-3') }}</li>
                                    <li>• {{ $t('forms.product.image-tip-4') }}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form Actions -->
                <div class="tw-border-t tw-border-gray-200 tw-pt-6 tw-mt-8">
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <!-- Cancel Button -->
                        <a-button
                            type="button"
                            intent="white"
                            @click="goBack"
                            :disabled="savingProduct"
                        >
                            {{ $t('common.cancel') }}
                        </a-button>
                        
                        <!-- Create Product Button -->
                        <a-button
                            type="submit"
                            intent="primary"
                            :loading="savingProduct"
                            :disabled="!productForm.title || !productForm.company_id || savingProduct"
                            left-icon="solar:add-circle-bold-duotone"
                            class="tw-min-w-[180px]"
                        >
                            {{ $t('forms.product.create-button') }}
                        </a-button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import FormField from '@/components/form/FormField.vue'
import FormSelect from '@/components/form/FormSelect.vue'
import FormMultiSelect from '@/components/form/FormMultiSelect.vue'
import useAlert from '@/composables/useAlert'
import productApi from '@/api/products'
import appApi from '@/api/app'
import companyApi from '@/api/companies'

const router = useRouter()
const { t } = useI18n()

// Form state
const savingProduct = ref(false)
const isDragging = ref(false)
const imageInput = ref(null)
const categoriesLoading = ref(false)
const companiesLoading = ref(false)
const categories = ref([])
const companies = ref([])

// Product form
const productForm = reactive({
    title: '',
    description: '',
    image: null,
    imagePreview: '',
    categories: [],
    company_id: ''
})

const productErrors = reactive({
    title: '',
    description: '',
    thumbnail_url: '',
    categories: '',
    company_id: ''
})

// Image handling methods
const triggerImageInput = () => {
    imageInput.value?.click()
}

const handleImageSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
        processSelectedImage(file)
    }
}

const handleImageDrop = (event) => {
    isDragging.value = false
    const file = event.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
        processSelectedImage(file)
    }
}

const processSelectedImage = (file) => {
    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
        useAlert('Image file size must be less than 5MB', 'warning')
        return
    }

    productForm.image = file
    
    // Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
        productForm.imagePreview = e.target.result
    }
    reader.readAsDataURL(file)
}

const removeImage = () => {
    productForm.image = null
    productForm.imagePreview = ''
    if (imageInput.value) {
        imageInput.value.value = ''
    }
}

// Computed
const categoryOptions = computed(() => {
    return categories.value.map(cat => ({
        id: cat.id,
        name: cat.name
    }))
})

const companyOptions = computed(() => {
    return [
        { id: '', name: t('forms.product.company-placeholder') },
        ...companies.value.map(company => ({
            id: company.id,
            name: company.name
        }))
    ]
})

// API Methods
const fetchCategories = async () => {
    categoriesLoading.value = true
    
    try {
        const response = await appApi.forms.categories('product')
        categories.value = response.data || []
    } catch (error) {
        console.error('Error fetching categories:', error)
        categories.value = []
        useAlert('Failed to load product categories', 'warning')
    } finally {
        categoriesLoading.value = false
    }
}

const fetchCompanies = async () => {
    companiesLoading.value = true
    
    try {
        // Use admin endpoint if available, otherwise fallback to regular endpoint
        const response = await companyApi.admin.all();
        
        companies.value = response.data?.companies || [];
    } catch (error) {
        console.error('Error fetching companies:', error)
        companies.value = []
        useAlert('Failed to load companies', 'warning')
    } finally {
        companiesLoading.value = false
    }
}

// Methods
const goBack = () => {
    router.push({ name: 'pages.admin.products' })
}

const validateProductForm = () => {
    // Reset errors
    Object.keys(productErrors).forEach(key => {
        productErrors[key] = ''
    })

    let isValid = true

    // Title validation
    if (!productForm.title.trim()) {
        productErrors.title = 'Product title is required'
        isValid = false
    } else if (productForm.title.trim().length < 3) {
        productErrors.title = 'Product title must be at least 3 characters'
        isValid = false
    } else if (productForm.title.trim().length > 100) {
        productErrors.title = 'Product title must be less than 100 characters'
        isValid = false
    }

    // Company validation
    if (!productForm.company_id) {
        productErrors.company_id = 'Please select a company'
        isValid = false
    }

    // Description validation
    if (productForm.description.trim().length > 1000) {
        productErrors.description = 'Description must be less than 1000 characters'
        isValid = false
    }

    return isValid
}

const createProduct = async () => {
    if (!validateProductForm()) {
        useAlert('Please fix the form errors', 'warning')
        return
    }

    savingProduct.value = true

    try {
        // Create FormData for file upload
        const formData = new FormData()
        
        // Add required fields
        formData.append('title', productForm.title.trim())
        formData.append('description', productForm.description.trim())
        formData.append('company_id', productForm.company_id)
        
        // Add image if selected
        if (productForm.image) {
            formData.append('thumbnail', productForm.image)
        }
        
        // Add categories as JSON array
        if (productForm.categories && productForm.categories.length > 0) {
            const categoryIds = productForm.categories.map(cat => cat.id)
            formData.append('category_ids', JSON.stringify(categoryIds))
        }

        // Call the API - use admin endpoint if available
        const response = productApi.admin && productApi.admin.create 
            ? await productApi.admin.create(formData)
            : await productApi.create(formData)
        
        if (response.status === 200 || response.status === 201) {
            // Show success message
            const successMessage = response.data?.message || 'Product created successfully!'
            useAlert(successMessage, 'success')
            
            // Navigate back to products list
            router.push({ name: 'pages.admin.products' })
        } else {
            // Handle API response errors
            const message = response.data?.message || 'Failed to create product'
            useAlert(message, 'danger')
        }

    } catch (error) {
        console.error('Error creating product:', error)
        
        // Handle different types of errors
        if (error.response?.status === 422) {
            // Validation errors
            const errors = error.response.data?.errors || {}
            
            // Map backend errors to form errors
            Object.keys(errors).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(productErrors, key)) {
                    productErrors[key] = errors[key][0] || errors[key]
                }
            })
            
            useAlert('Please correct the form errors', 'warning')
        } else if (error.response?.status === 401) {
            useAlert('Authentication required. Please log in again.', 'danger')
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to create products.', 'danger')
        } else if (error.response?.status >= 500) {
            useAlert('Server error. Please try again later.', 'danger')
        } else if (error.code === 'NETWORK_ERROR' || !error.response) {
            useAlert('Network error. Please check your connection and try again.', 'danger')
        } else {
            const message = error.response?.data?.message || 'Error creating product. Please try again.'
            useAlert(message, 'danger')
        }
    } finally {
        savingProduct.value = false
    }
}

// Initialize component
onMounted(async () => {
    await Promise.all([
        fetchCategories(),
        fetchCompanies()
    ])
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style>